import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from "react";
import AppContext from "./appContext";
import AttachmentsContext from "./attachmentsContext";
import { releaseFlowReducer, initialState } from "./releaseFlowReducer";
import { TYPES } from "./releaseFlowTypes";
import {
  groupAttachmentsForRelease,
  findReleasedSiblingVersions,
} from "./releaseFlow.utils";
import { getAttachmentSelectionKey } from "../utils/getAttachmentSelectionKey";
import { releaseAttachmentsBatch } from "../services/api/attachments/attachments.api";
import { useFeatureFlag } from "../hooks/useFeatureFlag";
import ReleaseFlowModal from "../components/ReleaseFlowModal/ReleaseFlowModal";
import {
  getBulkReleaseToastDescription,
  toastError,
  toastSuccess,
  PERMISSION_DENIED_MESSAGE,
} from "../utils";

const ReleaseFlowContext = createContext();

const DESTINATIONS = ["channel", "production", "cultureHub"];
const RELEASE_TYPE_BY_DESTINATION = {
  channel: "Channel",
  production: "Production",
  cultureHub: "CultureHub",
};

const seedDestinationState = (attachment) => ({
  channel: !!attachment?.releaseManagement?.channel,
  production: !!attachment?.releaseManagement?.production,
  cultureHub: !!attachment?.releaseManagement?.cultureHub,
});

// The AttachmentsScheduler pipeline already sets `versionNumber` on every
// attachment before it reaches here (see release-flow.md). The ProjectSheet
// (ContentAttachments/TableListFiles) pipeline never renamed its field and
// still only carries the raw `version` (e.g. `attachmentsContext.js`'s
// `compiledChapter`/grouping reads `u.version`), so `ReleaseFlowVersionLabel`
// would render "undefinedª Versão" for that flow. Normalize once here, at
// the single source feeding both flow steps, instead of touching every
// ProjectSheet call site.
const withVersionNumber = (attachment) => ({
  ...attachment,
  versionNumber: attachment?.versionNumber ?? attachment?.version,
});

// Merges per-destination attachmentId lists into the new batch API shape,
// combining destinations into a single releaseType array whenever an
// attachment shares the same observation across destinations.
const buildReleaseEntries = (idsAndObservationByDestination) => {
  const entriesByMapKey = new Map();

  DESTINATIONS.forEach((destination) => {
    const { ids = [], observation = "" } =
      idsAndObservationByDestination[destination] || {};

    ids.forEach((attachmentId) => {
      const mapKey = `${attachmentId}::${observation}`;
      if (!entriesByMapKey.has(mapKey)) {
        entriesByMapKey.set(mapKey, {
          attachmentId,
          observation,
          releaseType: [],
        });
      }
      entriesByMapKey
        .get(mapKey)
        .releaseType.push(RELEASE_TYPE_BY_DESTINATION[destination]);
    });
  });

  return Array.from(entriesByMapKey.values());
};

export const ReleaseFlowProvider = ({ children }) => {
  const [state, dispatch] = useReducer(releaseFlowReducer, initialState);
  const appContext = useContext(AppContext);
  const attachmentContext = useContext(AttachmentsContext);

  // Single source of truth for the whole feature: fetched once here instead
  // of each consumer (step components) calling useFeatureFlag independently,
  // which would fire duplicate requests and could momentarily disagree while
  // each hook instance resolves separately.
  //
  // Gated on `state.isOpen` rather than fetched eagerly at mount: this
  // provider sits above `<Routes />` in App.js and never unmounts, so an
  // unconditional fetch only ever ran once for the whole session - if auth
  // hadn't hydrated yet at that exact moment, the flag silently defaulted to
  // false and never retried, even after the user logged in and navigated
  // around. Deferring the request until the modal actually opens guarantees
  // a valid session and gets a fresh value on every open.
  const {
    enabled: productionReleaseEnabled,
    loading: productionReleaseLoading,
  } = useFeatureFlag("TEXT_RELEASE_V2", { enabled: state.isOpen });

  const {
    enabled: cultureHubReleaseEnabled,
    loading: cultureHubReleaseLoading,
  } = useFeatureFlag("CULTURE_HUB_RELEASE", { enabled: state.isOpen });

  // Feature flag required per destination. Destinations without an entry
  // here are always enabled. When a new gated destination is introduced,
  // add its `useFeatureFlag` call above and a row here - no branching logic
  // needs to change.
  const enabledDestinations = useMemo(() => {
    const destinationFeatureFlags = {
      production: productionReleaseEnabled,
      cultureHub: cultureHubReleaseEnabled,
    };

    return DESTINATIONS.filter(
      (destination) => destinationFeatureFlags[destination] ?? true
    );
  }, [productionReleaseEnabled, cultureHubReleaseEnabled]);

  const openReleaseFlow = useCallback(
    ({
      attachments = [],
      contentId,
      reload = () => {},
      onSuccess = () => {},
      allData = [],
      type,
      condition,
      groups,
    }) => {
      // An addendum can be associated with more than one chapter/episode, so
      // the same underlying attachment may appear more than once in
      // `attachments` (once per association). Dedupe by selection key here,
      // at the single source feeding every consumer (both flow steps), so
      // it isn't listed multiple times.
      const seenKeys = new Set();
      const uniqueAttachments = attachments
        .filter((attachment) => {
          const key = getAttachmentSelectionKey(attachment);
          if (!key || seenKeys.has(key)) return false;
          seenKeys.add(key);
          return true;
        })
        .map(withVersionNumber);

      const releaseGroups =
        groups ??
        groupAttachmentsForRelease(allData, attachmentContext, type, condition);
      const siblingsByKey = findReleasedSiblingVersions(
        uniqueAttachments,
        releaseGroups
      );

      const destinations = {};
      const originalDestinations = {};
      uniqueAttachments.forEach((attachment) => {
        const key = getAttachmentSelectionKey(attachment);
        if (!key) return;
        const seeded = seedDestinationState(attachment);
        destinations[key] = { ...seeded };
        originalDestinations[key] = { ...seeded };
      });

      const oldVersions = {};
      const keep = {};
      const originalKeep = {};
      Object.entries(siblingsByKey).forEach(([key, sibling]) => {
        oldVersions[key] = withVersionNumber(sibling);
        const seeded = seedDestinationState(sibling);
        keep[key] = { ...seeded };
        originalKeep[key] = { ...seeded };
      });

      dispatch({
        type: TYPES.OPEN,
        payload: {
          attachments: uniqueAttachments,
          contentId,
          reload,
          onSuccess,
          destinations,
          originalDestinations,
          oldVersions,
          keep,
          originalKeep,
        },
      });
    },
    [attachmentContext]
  );

  const closeReleaseFlow = useCallback(
    () => dispatch({ type: TYPES.CLOSE }),
    []
  );
  const goToVersionManagement = useCallback(
    () => dispatch({ type: TYPES.GO_TO_VERSION_MANAGEMENT }),
    []
  );
  const goBackToDestination = useCallback(
    () => dispatch({ type: TYPES.GO_BACK_TO_DESTINATION }),
    []
  );

  const toggleDestination = useCallback(
    (key, destination) =>
      dispatch({
        type: TYPES.TOGGLE_DESTINATION,
        payload: { key, destination },
      }),
    []
  );

  const toggleAllDestinations = useCallback(
    (destination, checked) =>
      dispatch({
        type: TYPES.TOGGLE_ALL_DESTINATIONS,
        payload: { destination, checked },
      }),
    []
  );

  const toggleKeepVersion = useCallback(
    (key, destination) =>
      dispatch({
        type: TYPES.TOGGLE_KEEP_VERSION,
        payload: { key, destination },
      }),
    []
  );

  const toggleKeepAllVersions = useCallback(
    (destination, checked) =>
      dispatch({
        type: TYPES.TOGGLE_KEEP_ALL_VERSIONS,
        payload: { destination, checked },
      }),
    []
  );

  const setObservationEnabled = useCallback(
    (destination, enabled) =>
      dispatch({
        type: TYPES.SET_OBSERVATION_ENABLED,
        payload: { destination, enabled },
      }),
    []
  );

  const setObservationText = useCallback(
    (destination, text) =>
      dispatch({
        type: TYPES.SET_OBSERVATION_TEXT,
        payload: { destination, text },
      }),
    []
  );

  const submitRelease = useCallback(() => {
    const selectedByKey = {};
    state.attachments.forEach((attachment) => {
      const key = getAttachmentSelectionKey(attachment);
      if (key) selectedByKey[key] = attachment;
    });

    // Defense in depth: the API errors out if asked to release a
    // destination that's already released, so build a lookup of "already
    // released" straight from each attachment's own `releaseManagement`
    // (the real source of truth) - guards against `originalDestinations`
    // ever going stale relative to the actual attachment data.
    const alreadyReleasedByAttachmentId = {};
    [...state.attachments, ...Object.values(state.oldVersions)].forEach(
      (attachment) => {
        if (attachment?.id == null) return;
        alreadyReleasedByAttachmentId[attachment.id] = {
          channel: !!attachment.releaseManagement?.channel,
          production: !!attachment.releaseManagement?.production,
        };
      }
    );

    const releasesByDestination = {};
    const unreleasesByDestination = {};

    // Defense in depth: never build a payload for a destination whose
    // feature flag is off, even if some future/legacy code path manages to
    // toggle it.
    enabledDestinations.forEach((destination) => {
      const releaseIds = Object.entries(state.destinations)
        .filter(
          ([key, value]) =>
            value[destination] &&
            !state.originalDestinations[key]?.[destination]
        )
        .map(([key]) => selectedByKey[key]?.id)
        .filter(
          (id) =>
            id != null && !alreadyReleasedByAttachmentId[id]?.[destination]
        );

      const unreleaseIdsFromSelection = Object.entries(state.destinations)
        .filter(
          ([key, value]) =>
            !value[destination] &&
            state.originalDestinations[key]?.[destination]
        )
        .map(([key]) => selectedByKey[key]?.id)
        .filter((id) => id != null);

      const unreleaseIdsFromOldVersions = Object.entries(state.keep)
        .filter(
          ([key, value]) =>
            !value[destination] && state.originalKeep[key]?.[destination]
        )
        .map(([key]) => state.oldVersions[key]?.id)
        .filter((id) => id != null);

      const observation = state.observationEnabled[destination]
        ? state.observationText[destination]
        : "";

      releasesByDestination[destination] = { ids: releaseIds, observation };
      unreleasesByDestination[destination] = {
        ids: [...unreleaseIdsFromSelection, ...unreleaseIdsFromOldVersions],
        observation,
      };
    });

    const releases = buildReleaseEntries(releasesByDestination);
    const unreleases = buildReleaseEntries(unreleasesByDestination);

    if (!releases.length && !unreleases.length) {
      state.onSuccess();
      closeReleaseFlow();
      return;
    }

    dispatch({ type: TYPES.SET_SUBMITTING, payload: { isSubmitting: true } });

    releaseAttachmentsBatch({
      contentId: state.contentId,
      releases,
      unreleases,
    })
      .then(() => {
        dispatch({
          type: TYPES.SET_SUBMITTING,
          payload: { isSubmitting: false },
        });
        toastSuccess(
          "Liberação de anexos",
          getBulkReleaseToastDescription({
            selectedCount: state.attachments.length,
            releases,
          })
        );
        state.reload();
        state.onSuccess();
        closeReleaseFlow();
      })
      .catch((error) => {
        dispatch({
          type: TYPES.SET_SUBMITTING,
          payload: { isSubmitting: false },
        });
        toastError(
          error?.response?.status === 403
            ? PERMISSION_DENIED_MESSAGE
            : "Erro ao liberar anexos!"
        );
      });
  }, [state, closeReleaseFlow, enabledDestinations]);

  const value = useMemo(
    () => ({
      ...state,
      productionReleaseEnabled,
      productionReleaseLoading,
      cultureHubReleaseEnabled,
      cultureHubReleaseLoading,
      openReleaseFlow,
      closeReleaseFlow,
      goToVersionManagement,
      goBackToDestination,
      toggleDestination,
      toggleAllDestinations,
      toggleKeepVersion,
      toggleKeepAllVersions,
      setObservationEnabled,
      setObservationText,
      submitRelease,
    }),
    [
      state,
      productionReleaseEnabled,
      productionReleaseLoading,
      cultureHubReleaseEnabled,
      cultureHubReleaseLoading,
      openReleaseFlow,
      closeReleaseFlow,
      goToVersionManagement,
      goBackToDestination,
      toggleDestination,
      toggleAllDestinations,
      toggleKeepVersion,
      toggleKeepAllVersions,
      setObservationEnabled,
      setObservationText,
      submitRelease,
    ]
  );

  return (
    <ReleaseFlowContext.Provider value={value}>
      {children}
      <ReleaseFlowModal />
    </ReleaseFlowContext.Provider>
  );
};

export default ReleaseFlowContext;
